import React, { useState } from 'react';
import { Ship, Plane, Truck, Globe, Package, CheckCircle, Mail, Phone, MapPin, Menu, X, MessageCircle } from 'lucide-react';
import './App.css';

function App() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your inquiry. We will contact you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };


  const openWhatsApp = () => {
    window.open('https://wa.me/61474640538', '_blank');
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <h1 className="text-xl sm:text-2xl font-bold text-green-800">BHULLAR AGROPORTERS</h1>
              <p className="text-xs text-gray-600 hidden sm:block">Premium Agro-Logistics</p>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-green-700 transition">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-green-700 transition">Services</button>
              <button onClick={() => scrollToSection('why-us')} className="text-gray-700 hover:text-green-700 transition">Why Us</button>
              <button onClick={() => scrollToSection('contact')} className="bg-green-700 text-white px-6 py-2 rounded-lg hover:bg-green-800 transition">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-700">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50">Services</button>
              <button onClick={() => scrollToSection('why-us')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50">Why Us</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50">Contact</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80" 
            alt="Cargo ships" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-green-800/85 to-green-950/90"></div>
        </div>
        
        <div className="relative z-20 text-center px-4 sm:px-6 max-w-5xl mx-auto text-white">
          <div className="mb-6 inline-block">
            <Globe className="w-16 h-16 sm:w-20 sm:h-20 text-amber-400 mx-auto mb-4" style={{animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'}} />
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
            BHULLAR AGROPORTERS<br />LINE PRIVATE LIMITED
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl mb-12 text-green-100 font-light tracking-wide">
            Moving Agricultural Goods Across Borders with Trust & Precision
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('contact')} className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold px-8 py-4 rounded-lg text-lg transition transform hover:scale-105 shadow-lg">
              Contact Us
            </button>
            <button onClick={() => scrollToSection('services')} className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border-2 border-white text-white font-semibold px-8 py-4 rounded-lg text-lg transition">
              Our Services
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">About Our Company</h2>
            <div className="w-24 h-1 bg-green-700 mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <span className="font-bold text-green-800">BHULLAR AGROPORTERS LINE PRIVATE LIMITED</span> is a growing Indian logistics enterprise incorporated on <span className="font-semibold">12 July 2024</span>, operating from Haryana, India.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The company focuses on transportation and trade logistics of agricultural commodities, ensuring safe, compliant, and timely delivery across national and international routes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Operations are managed under <span className="font-semibold text-green-800">Harmeetpal Singh Bhullar's</span> operated transport lines, focusing on reliable, compliant, and efficient agro-commodity movement.
              </p>

              <div className="mt-8 bg-gradient-to-br from-green-50 to-amber-50 p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Our Transport Networks</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Ship className="text-green-700 w-5 h-5" />
                    <span className="text-gray-700 text-sm font-medium">Sea Ports</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Plane className="text-green-700 w-5 h-5" />
                    <span className="text-gray-700 text-sm font-medium">Air Ports</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Truck className="text-green-700 w-5 h-5" />
                    <span className="text-gray-700 text-sm font-medium">Land Ports</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Globe className="text-green-700 w-5 h-5" />
                    <span className="text-gray-700 text-sm font-medium">Multi-Modal</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=80" 
                alt="Agricultural produce and logistics" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80" 
            alt="Logistics background" 
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <div className="w-24 h-1 bg-green-700 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive logistics solutions tailored for agricultural commodity transportation
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                icon: <Package className="w-12 h-12" />, 
                title: 'Agricultural Goods Transportation', 
                desc: 'Specialized handling and transport of grains, pulses, and farm produce',
                img: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=600&q=80'
              },
              { 
                icon: <Ship className="w-12 h-12" />, 
                title: 'Sea Port Logistics', 
                desc: 'International shipping through major Indian sea ports with full compliance',
                img: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=600&q=80'
              },
              { 
                icon: <Plane className="w-12 h-12" />, 
                title: 'Air Cargo Handling', 
                desc: 'Fast, reliable air freight solutions for time-sensitive agro exports',
                img: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80'
              },
              { 
                icon: <Truck className="w-12 h-12" />, 
                title: 'Land & Road Transport', 
                desc: 'Pan-India road network connectivity with GPS-tracked vehicles',
                img: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=80'
              },
              { 
                icon: <Globe className="w-12 h-12" />, 
                title: 'Multi-Modal Trade Routes', 
                desc: 'Seamless integration of sea, air, and land transport channels',
                img: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=600&q=80'
              },
              { 
                icon: <CheckCircle className="w-12 h-12" />, 
                title: 'End-to-End Supply Chain', 
                desc: 'Complete logistics support from origin to final destination',
                img: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=600&q=80'
              }
            ].map((service, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.img} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    {service.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1560264280-88b68371db39?w=1920&q=80" 
            alt="Logistics warehouse" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/95 via-green-800/90 to-green-950/95"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16 text-white">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Why Choose Us</h2>
            <div className="w-24 h-1 bg-amber-400 mx-auto"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Trusted Operations', desc: 'Built on integrity and professional excellence', icon: <CheckCircle className="w-10 h-10" /> },
              { title: 'Agro Expertise', desc: 'Specialized knowledge in agricultural logistics', icon: <Package className="w-10 h-10" /> },
              { title: 'Multi-Route Capability', desc: 'Sea, air, and land transport flexibility', icon: <Globe className="w-10 h-10" /> },
              { title: 'Compliance Driven', desc: 'Full adherence to national and international regulations', icon: <CheckCircle className="w-10 h-10" /> }
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur-md rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <div className="bg-amber-400/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 border-2 border-amber-400 text-amber-400">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                <p className="text-green-100">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

       <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-16">
                  <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Let's Move Your Cargo Forward</h2>
                  <div className="w-24 h-1 bg-green-700 mx-auto mb-6"></div>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Your reliable partner for safe, compliant, and timely agricultural commodity transportation across India and beyond
                  </p>
                </div>
      
                {/* Value Proposition & Response Time */}
                <div className="bg-gradient-to-r from-green-700 to-green-800 text-white rounded-2xl p-8 mb-12 shadow-xl">
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div>
                      <CheckCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                      <h3 className="text-xl font-bold mb-2">Fast Response</h3>
                      <p className="text-green-100">We respond to all inquiries within 24 business hours</p>
                    </div>
                    <div>
                      <Package className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                      <h3 className="text-xl font-bold mb-2">Custom Solutions</h3>
                      <p className="text-green-100">Tailored logistics plans for your specific commodity needs</p>
                    </div>
                    <div>
                      <Globe className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                      <h3 className="text-xl font-bold mb-2">Multi-Modal Routes</h3>
                      <p className="text-green-100">Access to sea, air, and land transportation networks</p>
                    </div>
                  </div>
                </div>
      
                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-2 gap-12 mb-12">
                  {/* Left Column - Contact Methods */}
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">How Can We Help You Today?</h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">
                        Whether you're looking to transport grains, pulses, or other agricultural commodities, our experienced team is ready to design a logistics solution that meets your timeline, budget, and compliance requirements. We handle everything from documentation to final delivery.
                      </p>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Choose the contact method that works best for you below. Our logistics specialists will guide you through service options, provide transparent pricing, and answer any questions about our sea port, air cargo, or land transport capabilities.
                      </p>
                    </div>
      
                    {/* Contact Methods */}
                    <div className="space-y-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Choose Your Preferred Contact Method</h4>
                      
                      {/* Email Options */}
                      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition">
                        <div className="flex items-start space-x-4">
                          <div className="bg-green-100 p-3 rounded-lg">
                            <Mail className="w-6 h-6 text-green-700" />
                          </div>
                          <div className="flex-1">
                            <h5 className="text-lg font-bold text-gray-900 mb-2">Email Us</h5>
                            <p className="text-gray-600 mb-3">Send us a detailed inquiry and we'll respond within 24 hours</p>
                            <div className="space-y-2">
                              <a 
                                href="mailto:hsbhulla@gmail.com?subject=General Inquiry About Agricultural Logistics Services&body=Hello BHULLAR AGROPORTERS Team,%0D%0A%0D%0AI am interested in learning more about your agricultural commodity transportation services.%0D%0A%0D%0APlease provide information about:%0D%0A- Available routes and transport modes%0D%0A- Pricing structure%0D%0A- Documentation requirements%0D%0A%0D%0AThank you."
                                className="inline-flex items-center text-green-700 hover:text-green-800 font-semibold transition group"
                              >
                                <span className="group-hover:underline">General Inquiries →</span>
                              </a>
                              <br />
                              <a 
                                href="mailto:hsbhulla@gmail.com?subject=Request for Shipping Quote&body=Hello,%0D%0A%0D%0AI would like to request a quote for transporting agricultural commodities.%0D%0A%0D%0AShipment Details:%0D%0A- Commodity Type: %0D%0A- Quantity: %0D%0A- Origin: %0D%0A- Destination: %0D%0A- Preferred Transport Mode: %0D%0A- Timeline: %0D%0A%0D%0APlease provide pricing and estimated delivery time.%0D%0A%0D%0AThank you."
                                className="inline-flex items-center text-green-700 hover:text-green-800 font-semibold transition group"
                              >
                                <span className="group-hover:underline">Request a Quote →</span>
                              </a>
                              <br />
                              <a 
                                href="mailto:hsbhulla@gmail.com?subject=Partnership Opportunity&body=Hello,%0D%0A%0D%0AI am interested in exploring a potential partnership with BHULLAR AGROPORTERS.%0D%0A%0D%0APartnership Type: %0D%0ACompany/Organization: %0D%0AContact Person: %0D%0A%0D%0AI would like to discuss how we can collaborate.%0D%0A%0D%0AThank you."
                                className="inline-flex items-center text-green-700 hover:text-green-800 font-semibold transition group"
                              >
                                <span className="group-hover:underline">Business Partnerships →</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
      
                      {/* WhatsApp */}
                      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition">
                        <div className="flex items-start space-x-4">
                          <div className="bg-green-100 p-3 rounded-lg">
                            <MessageCircle className="w-6 h-6 text-green-700" />
                          </div>
                          <div className="flex-1">
                            <h5 className="text-lg font-bold text-gray-900 mb-2">WhatsApp Chat</h5>
                            <p className="text-gray-600 mb-3">Quick questions? Chat with us on WhatsApp</p>
                            <button
                              onClick={openWhatsApp}
                              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition inline-flex items-center space-x-2"
                            >
                              <MessageCircle className="w-5 h-5" />
                              <span>Start WhatsApp Chat</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
      
                  {/* Right Column - Company Info & Trust Signals */}
                  <div className="space-y-8">
                    <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl">
                      <img 
                        src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80" 
                        alt="BHULLAR AGROPORTERS logistics operations" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-green-900/90 to-transparent"></div>
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        <h3 className="text-2xl font-bold mb-2">Trusted Since July 2024</h3>
                        <p className="text-green-100">Operating from Haryana's agricultural heartland</p>
                      </div>
                    </div>
      
                    {/* What Happens Next */}
                    <div className="bg-gradient-to-br from-amber-50 to-green-50 rounded-xl p-6 border border-amber-200">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">What Happens After You Contact Us?</h4>
                      <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                          <div className="bg-green-700 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                            1
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Immediate Confirmation</p>
                            <p className="text-gray-600 text-sm">You'll receive an acknowledgment within 2-4 hours during business hours</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-green-700 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                            2
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Detailed Consultation</p>
                            <p className="text-gray-600 text-sm">Our logistics specialist reviews your requirements and prepares options</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-green-700 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                            3
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Custom Proposal</p>
                            <p className="text-gray-600 text-sm">Receive transparent pricing, route details, and timeline within 24 hours</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-green-700 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                            4
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Seamless Execution</p>
                            <p className="text-gray-600 text-sm">Once approved, we handle documentation, pickup, and safe delivery</p>
                          </div>
                        </div>
                      </div>
                    </div>
      
                    {/* Service Areas */}
                    <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Our Service Coverage</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">Pan-India Network</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">Major Sea Ports</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">International Routes</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">Air Cargo Terminals</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">Land Border Posts</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                          <span className="text-gray-700 text-sm">Multi-Modal Hubs</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

      {/* Footer with Company Details */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-8 text-center">Company Details</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 bg-gray-800 rounded-xl p-8">
              <div>
                <p className="text-sm text-gray-400 mb-1">Corporate Identification Number</p>
                <p className="text-base font-semibold">U47912HR2024PTC123306</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Company Type</p>
                <p className="text-base font-semibold">Private Limited Company</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Industry</p>
                <p className="text-base font-semibold">Agro Trade & Logistics</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Registered Office</p>
                <p className="text-base font-semibold">Nilokheri, Haryana, India</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Status</p>
                <p className="text-base font-semibold text-green-400">Active</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Date of Incorporation</p>
                <p className="text-base font-semibold">12 July 2024</p>
              </div>
            </div>
          </div>

         <div className="border-t border-gray-700 pt-8 flex flex-col items-center gap-6 text-center">
          <div>
            <h3 className="text-2xl font-bold mb-2">BHULLAR AGROPORTERS LINE PRIVATE LIMITED</h3>
            <p className="text-gray-400 mb-2">Moving Agricultural Goods Across Borders with Trust & Precision</p>
            <p className="text-gray-400 text-sm">
              © 2025 BHULLAR AGROPORTERS LINE PRIVATE LIMITED. All Rights Reserved.
            </p>
          </div>

          <a
            href="https://www.launzr.in"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-gray-300 hover:text-accent transition-colors text-xs group"
          >
            <span>Tech Partner</span>
            <div className="relative w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform">
              <img
                src="https://launzr.in/images/logo.jpg"
                alt="Launzr Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-medium">Launzr</span>
          </a>
        </div>

        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all duration-300 z-50"
        style={{animation: 'bounce 1s infinite'}}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );
}

export default App;